import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { IntegrationPageActionsProps } from '../IntegrationPage';
import IspVisionApiKeyForm from './IspVisionApiKeyForm';
import ApiTierOrderModal from '../../api/ApiTierOrderModal';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { saveData } from 'services/api/saveData';
import { canOrderPaidFeatures } from '../../billing/functions/billingUtils';

const IspVisionIntegrationPageActions: React.FC<IntegrationPageActionsProps> = ({ handleRefetch }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { environmentType, activeFeatures } = useAuthContext();
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);

    // Open the api tier order modal after the api key is created, non ordering environment types keep the free tier
    const openTierOrderModal = (data?: Record<string, any>) => {
        if (!canOrderPaidFeatures(environmentType) || !data?.id) return;
        initializeModal(
            <ApiTierOrderModal tokenId={data.id} onSuccess={handleRefetch} />,
            { modalSize: 'medium' }
        );
    };

    // Open the division selection form, only needed when the divisions feature is active
    const openApiKeyForm = () => {
        initializeModal(
            <IspVisionApiKeyForm />, {
                title: t('integrations.isp_vision.key_modal_title'),
                modalSize: 'small-medium',
                onSuccess: openTierOrderModal
            }
        );
    };

    // Create the api key directly and continue to the tier order modal
    const createApiKeyDirectly = async () => {
        try {
            // Start the button loader
            setButtonLoader(true);

            // Create the isp vision api key, the backend sets the default name
            const response = await saveData({
                apiUrl: 'post_api_token',
                method: 'post',
                data: { source: 'isp_vision' }
            });

            // Stop the button loader
            setButtonLoader(false);

            // Refresh the key list and continue to the tier order modal
            if (response?.status === 201) {
                handleRefetch?.();
                openTierOrderModal(response.data);
            }
        } catch (error) {
            console.error('Error on isp vision key creation', error);

            // Stop the button loader
            setButtonLoader(false);

            // Show an alert that the key could not be created
            setFloatingAlert({ type: 'danger', message: 'integrations.isp_vision.key_creation_failed_error' });
        };
    };

    // Show the division form when divisions are active, otherwise create the key directly
    const handleOrderIntegration = () => {
        if (activeFeatures.includes('divisions')) {
            openApiKeyForm();
        } else {
            createApiKeyDirectly();
        }
    };

    return (
        <div className="integration-actions">
            <PrimaryButton
                label="integrations.general.order_integration_label"
                loading={buttonLoader}
                onClick={handleOrderIntegration}
            />
        </div>
    );
};

export default IspVisionIntegrationPageActions;
